/** @jsx jsx */
import { css, jsx } from '@emotion/core'
import { Heading, HFlow, Icon, Tag, Text, Theme, useTheme, VFlow } from 'bold-ui'
import { IconDateRange } from 'components/IconDateRange'
import { ReceitaMedicamento, TipoReceitaEnum, UnidadeMedidaTempoEnum } from 'graphql/types.generated'
import React from 'react'
import { formatDate } from 'util/date/formatDate'
import { TipoReceitaTag } from 'view/atendimentos/detail/components/TipoReceitaTag'
import { periodoTratamento } from 'view/atendimentos/detail/soap/plano/prescricao-medicamento/components/PeriodoTratamentoSelectField'

export const getTextoDataFimTratamento = (receita: ReceitaMedicamento): string => {
  const textoPeriodoPadrao = ` ${periodoTratamento[receita.medidaTempoTratamento]}`

  if (receita.atendProf?.versaoAnterior) {
    return textoPeriodoPadrao
  }

  const temDataFimValida =
    receita.medidaTempoTratamento !== UnidadeMedidaTempoEnum.INDETERMINADO && receita.dtFimTratamento

  if (temDataFimValida) {
    return formatDate(receita.dtFimTratamento)
  }

  return textoPeriodoPadrao
}

interface MedicamentosAccordionItemHeaderProps {
  readonly receita: ReceitaMedicamento
  readonly dataInicioGrupo: string
  readonly hasInterrompidoAgora?: boolean
}

function MedicamentosAccordionItemHeaderInternal(props: MedicamentosAccordionItemHeaderProps) {
  const { receita, dataInicioGrupo, hasInterrompidoAgora = false } = props
  const theme = useTheme()
  const styles = createStyles(theme)

  return (
    <HFlow justifyContent='space-between' style={styles.headerFlow}>
      <VFlow vSpacing={0.2}>
        <HFlow hSpacing={0.5} alignItems='center'>
          <Heading level={5}>
            {`${receita.medicamento.principioAtivo} ${receita.medicamento.concentracao ?? ''}`.trim()}
          </Heading>
          {hasInterrompidoAgora && (
            <div css={styles.interrompidoAgoraStyles}>
              <Icon
                icon='clockOutline'
                size={1}
                style={css`
                  margin-right: 0.5rem;
                `}
                data-cy='MedicamentoAtivosConcluidos.InterrompidoAgora'
              />
              Interrompido agora
            </div>
          )}
        </HFlow>
        <div>
          {receita.usoContinuo && (
            <Tag
              type='normal'
              style={css`
                margin-right: 0.5rem;
              `}
            >
              Uso contínuo
            </Tag>
          )}
          {receita.tipoReceita !== TipoReceitaEnum.COMUM && (
            <TipoReceitaTag
              tipoReceita={receita.tipoReceita}
              style={css`
                margin-right: 0.5rem;
              `}
            />
          )}
          {receita.tratamentoInterrompido && (
            <Tag type='normal' data-cy='MedicamentoAtivosConcluidos.TratamentoInterrompido'>
              Tratamento interrompido
            </Tag>
          )}
        </div>
        <Text>{receita.posologia}</Text>
      </VFlow>
      <IconDateRange startDate={formatDate(dataInicioGrupo)} endDate={getTextoDataFimTratamento(receita)} />
    </HFlow>
  )
}

export const MedicamentosAccordionItemHeader = React.memo(MedicamentosAccordionItemHeaderInternal)

const createStyles = (theme: Theme) => ({
  headerFlow: css`
    flex: 1;
  `,
  interrompidoAgoraStyles: css`
    color: ${theme.pallete.primary.main};
    display: inline-flex;
    align-items: center;
    font-weight: normal;
  `,
})
